/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useParams, useNavigate } from 'react-router-dom';
import { MonthlyDetailResponse } from '../../types';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ParticipantType } from '../../types';
import './ActivityReport.css';

const MonthlyDetailPage: React.FC = () => {
  const { yearMonth } = useParams<{ yearMonth: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [detail, setDetail] = useState<MonthlyDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDetail();
  }, [yearMonth]);

  const fetchDetail = async () => {
    if (!yearMonth) {
      setError('Invalid month parameter');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let response: MonthlyDetailResponse;
      if (currentUser?.type === ParticipantType.ADMIN) {
        response = await apiClient.getMonthlyDetail(yearMonth);
      } else {
        response = await apiClient.getManagerMonthlyDetail(yearMonth);
      }

      if (response.success) {
        setDetail(response);
      } else {
        setError(response.error || 'Failed to load monthly details');
      }
    } catch (err: any) {
      console.error('Error fetching monthly detail:', err);
      setError(err.message || 'An error occurred while loading the monthly details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="activity-report-container">
      <BackButton />
        <div className="loading">Loading monthly details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-report-container">
        <div className="error-message">
          <h3>Error Loading Monthly Details</h3>
          <p>{error}</p>
          <button onClick={fetchDetail} className="retry-button">
            Retry
          </button>
          <button onClick={() => navigate(-1)} className="retry-button" style={{ marginLeft: '10px' }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="activity-report-container">
        <div className="error-message">No data available</div>
      </div>
    );
  }

  return (
    <div className="activity-report-container">
      <div className="report-header">
        <button onClick={() => navigate(-1)} className="retry-button" style={{ marginBottom: '20px' }}>
          Back to Report
        </button>
        <h2>Monthly Activity Detail - {detail.monthName}</h2>
        <div className="report-summary">
          <div className="summary-card">
            <div className="summary-label">Total Bids</div>
            <div className="summary-value">{detail.bids.length}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Components</div>
            <div className="summary-value">{detail.components.length}</div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <h3>Bids Created</h3>
        {detail.bids.length === 0 ? (
          <p>No bids created in this month</p>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Author</th>
                <th>Price</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {detail.bids.map((bid) => (
                <tr key={bid.id}>
                  <td>{bid.id}</td>
                  <td className="month-name">{bid.name}</td>
                  <td>{bid.authorName}</td>
                  <td>{bid.price ? `$${bid.price.toFixed(2)}` : 'N/A'}</td>
                  <td>{new Date(bid.createdDate).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-container" style={{ marginTop: '40px' }}>
        <h3>Components Uploaded</h3>
        {detail.components.length === 0 ? (
          <p>No components uploaded in this month</p>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Manufacturer</th>
                <th>Company</th>
                <th>Price</th>
                <th>Upload Date</th>
              </tr>
            </thead>
            <tbody>
              {detail.components.map((component) => (
                <tr key={component.id}>
                  <td>{component.id}</td>
                  <td className="month-name">{component.name}</td>
                  <td>{component.manufacturerName}</td>
                  <td>{component.companyName}</td>
                  <td>{component.price ? `$${component.price.toFixed(2)}` : 'N/A'}</td>
                  <td>{new Date(component.uploadDate).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MonthlyDetailPage;
